import type { DynamicFieldComponentProps, JsonDataItem } from '@myosh/odin-components';
import React, { useMemo } from 'react';
import { Collection, ListBox, ListLayout, Virtualizer } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Observable } from 'rxjs';
import type { LinkedRecordResult, RecordLinkEntity, RecordLinkProps } from '../../../@types/linked-records-field';
import type { Module } from '../../../@types/modules';
import type { RecordResult } from '../../../@types/records';
import useActiveRecordRef from '../../../hooks/use-active-record-ref';
import { forceAssert } from '../../../utils/type-guards';
import OdinLoadingIndicator from '../../common/odin-loading-indicator.component';
import { LinkedRecordRowProps } from '../../dynamic-page/odin-data-grid-row-template.component';
import { OdinMobileCardTemplate, returnRecordTitle } from '../../dynamic-page/odin-data-mobile-card-template.component';
import { RecordTitle } from '../../record/record-title.component';

export interface LinkedRecordsProps
  extends DynamicFieldComponentProps<Array<LinkedRecordResult>, Array<string>>,
    RecordLinkProps {
  isTextualQuestionnaireLinkedRecords?: boolean;
  saveRecord?: () => Observable<RecordResult>;
  data: JsonDataItem[];
  isLinkedRecordsFetching?: boolean;
  listOfPermittedRecordIds?: string[];
  pendingPermissions?: string[];
  modules?: Module[];
  gridColumns?: RecordLinkEntity[];
  removeLinkedRecord?: (id: string) => void;
  isRowClickable?: boolean;
  rowIndicator?: { indicator: (item: JsonDataItem, className?: string) => React.ReactNode };
}

export default function MobileLinkedBaseRecords({
  readOnly,
  isLinkedRecordsFetching = false,
  listOfPermittedRecordIds,
  pendingPermissions = [],
  modules,
  data: transformedData,
  removeLinkedRecord,
  gridColumns = [],
  isRowClickable = true,
  rowIndicator,
  label,
}: LinkedRecordsProps) {
  const { activeRecordReference } = useActiveRecordRef();
  const { t } = useTranslation();
  const recordFields: Record<string, string> = LinkedRecordRowProps;

  const handleSelectedRecord = (selectedItem?: JsonDataItem) => {
    if (!selectedItem) {
      return;
    }
    const module = selectedItem
      ? modules?.find(
          (moduleItem: unknown) => forceAssert<{ id: number }>(moduleItem).id === selectedItem[recordFields.moduleId]
        )
      : undefined;
    if (selectedItem && module) {
      const date = new Date(selectedItem[recordFields.date as string] as string | Date);
      const title = returnRecordTitle(selectedItem, recordFields);
      const details = <RecordTitle title={title} date={date} />;
      const recordId = selectedItem[recordFields.id];
      activeRecordReference?.current?.addActiveRecord(String(recordId), module.icon ?? '', details, false, {
        title,
        recordId: recordId,
        viewId: module.viewId,
        moduleIcon: module.icon,
        formId: selectedItem.formId,
        formName: selectedItem[recordFields.formName],
        docNo: selectedItem[recordFields.docNo],
        status: selectedItem[recordFields.status],
        vikingLinkUrl: selectedItem[recordFields.vikingLinkUrl],
        displayText: selectedItem['displayText'],
      });
    }
  };

  const disabledKeys: number[] = useMemo(
    () =>
      transformedData.map((item) => item.id as number).filter((id) => !listOfPermittedRecordIds?.includes(String(id))),
    [transformedData, listOfPermittedRecordIds]
  );

  const listBoxKey = useMemo(() => `list-linked-records-${label}`, [label]);
  const linkRecordList = isLinkedRecordsFetching ? (
    <OdinLoadingIndicator />
  ) : (
    <div className="bg-gray-50 h-full overflow-y-auto p-1">
      <Virtualizer
        layout={ListLayout}
        layoutOptions={{
          estimatedRowHeight: 160,
          gap: 8,
        }}
      >
        <ListBox
          dependencies={[disabledKeys]}
          key={listBoxKey}
          aria-label={t('linked-records')}
          disabledKeys={disabledKeys}
          onAction={(key) => {
            if (isRowClickable) {
              const selectedItem = transformedData.find((item) => item.id === key);
              handleSelectedRecord(selectedItem);
            }
          }}
          renderEmptyState={() =>
            transformedData.length === 0 || !isLinkedRecordsFetching ? (
              <div className="text-gray-500 p-6 text-center">
                <div className="text-base font-medium">{t('no-records-found', 'No records found')}</div>
              </div>
            ) : (
              <OdinLoadingIndicator />
            )
          }
        >
          <Collection items={transformedData} dependencies={[readOnly, listOfPermittedRecordIds]}>
            {(item: JsonDataItem) => (
              <OdinMobileCardTemplate
                item={item}
                readOnly={readOnly}
                rowIndicator={rowIndicator}
                isRowClickable={isRowClickable}
                gridColumns={gridColumns}
                removeLinkedRecord={removeLinkedRecord}
                listOfPermittedRecordIds={listOfPermittedRecordIds}
                pendingPermissions={pendingPermissions}
                listKey={listBoxKey}
              />
            )}
          </Collection>
        </ListBox>
      </Virtualizer>
    </div>
  );

  return (
    <>
      <div className="my-2 max-h-[40rem] overflow-auto">{linkRecordList}</div>
    </>
  );
}
